import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import cx from 'classnames'
import { useHistory, useLocation, generatePath } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { Button } from 'blaze-ui/Button'
import { Input } from 'almanac-ui/TextInputField/Input'
import { ReactComponent as RightArrowIcon } from 'assets/icons/arrowRight24.svg'
import { Email } from 'blaze-ui/icons/24'
import Close from 'blaze-ui/icons/20/Close'
import { Check } from 'blaze-ui/icons/16'
import { FormErrorMessage } from 'common/FormErrorMessage/FormErrorMessage'
import { UserClient } from 'api/clients/UserClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { authFromResponse } from 'utils/authFromResponse'
import client from 'api/client'
import { useSignUp } from 'screens/Registration/hooks/useSignUp'
import { useAuth } from 'hooks/UseAuth'
import { AUTOPILOT_REGISTRATION_PATH, LOGIN_PATH, SELECT_WORKSPACE_PATH, VIRTUAL_MARKETER_ONBOARDING_PATH } from 'paths'
import { GoogleOAuthButton } from 'flows/common/steps/Registration/GoogleOAuthButton/GoogleOAuthButton'
import { FacebookOAuthButton } from 'flows/common/steps/Registration/FacebookOAuthButton/FacebookOAuthButton'
import { AppleOAuthButton } from 'flows/common/steps/Registration/AppleOAuthButton/AppleOAuthButton'
import { WhiteLabelLogo } from 'components/WhiteLabelLogo/WhiteLabelLogo'
import { useInvitedWorkspace } from 'hooks/useInvitedWorkspace'
import { RegistrationType, trackContinueWithSignUp, trackVerifyWithCodeComplete } from 'screens/LoginOrSignup/utils'
import RegistrationStyles from 'flows/common/steps/Registration/Registration.module.scss'
import EnterPasswordStyles from 'flows/common/steps/EnterPassword/EnterPassword.module.scss'
import VerifyStyles from 'flows/common/steps/VerifyEmail/VerifyEmail.module.scss'
import CreateAccountStyles from 'flows/common/steps/CreateAccount/CreateAccount.module.scss'
import { CodeInput } from 'screens/Registration/CodeInput/CodeInput'
import { IS_VM_REGISTRATION_KEY } from 'flows/common/steps/Registration/constants'
import { Text } from 'blaze-ui/Text/Text'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import Styles from './Signup.module.scss'
import { BASE_ASSET_URL } from 'flows/common/constants'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { Referrer } from 'flows/common/steps/Referrer/Referrer'
import { BusinessType } from 'flows/common/steps/BusinessType/BusinessType'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useIsOnboardingRedesign } from 'hooks/useIsOnboardingRedesign'
import { useQueryClient } from 'react-query'
import useLoginOrSignUp from 'screens/LoginOrSignup/hooks/useLoginOrSignUp'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useSession } from 'hooks/useSession'
import { LoadingEllipses } from 'components/Wizards/Wizard/Form/SectionHeader'
import { useAutopilotProvider } from 'hooks/useAutopilotProvider'
import { observer } from 'mobx-react-lite'
import { Spinner } from 'common/Spinner/Spinner'
import { useRootStore } from 'stores/RootStore'
import { GetStarted } from 'flows/common/steps/GetStarted/GetStarted'
import { EyeClosed, EyeOpen } from 'blaze-ui/icons'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

type Stage = 'start' | 'verifyCode' | 'existingUser' | 'createAccount' | 'referrer' | 'businessType' | 'getStarted'

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const Signup = observer(() => {
  useAutopilotProvider()

  const { isMobile } = useResponsiveDesign()
  const isOnboardingRedesign = useIsOnboardingRedesign()
  const { featureFlags } = useRootStore()
  const history = useHistory()
  const location = useLocation()
  const { onSignUpSuccess } = useSignUp()
  const hasNavigatedRef = useRef(false)
  const auth = useAuth()

  const params = useMemo(() => new URLSearchParams(location.search), [location.search])
  const workspaceInviteToken = params.get('workspaceInviteToken') || params.get('workspace_invite_token') || undefined
  const redirectTo = params.get('redirect_to') || undefined
  const promotionCode = params.get('promotion_code') || undefined
  const queryClient = useQueryClient()
  const { logout } = useSession()

  const { workspace: invitedWorkspace, loading: invitedWorkspaceLoading } = useInvitedWorkspace(workspaceInviteToken)

  const workspaceIdFromParams = params.get('workspaceId')
  const emailFromParams = params.get('email')
  const isSSOUser = !!(workspaceIdFromParams && emailFromParams && auth?.id)

  const [stage, setStage] = useState<Stage>(isSSOUser ? 'referrer' : 'start')
  const [showEmailInput, setShowEmailInput] = useState(false)
  const [emailInput, setEmailInput] = useState(params.get('email') || '')
  const [emailError, setEmailError] = useState('')
  const [apiError, setApiError] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)

  const [accountAlreadyExists, setAccountAlreadyExists] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [codeError, setCodeError] = useState('')
  const [clearInputKey, setClearInputKey] = useState(0)
  const [showPassword, setShowPassword] = useState(false)

  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [nameError, setNameError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [accountError, setAccountError] = useState('')
  const [authInfo, setAuthInfo] = useState<any>(isSSOUser ? auth : null)
  const [landingWorkspace, setLandingWorkspace] = useState<any>(null)

  useEffect(() => {
    if (isSSOUser && workspaceIdFromParams) {
      client.getUserWorkspaces().then((res) => {
        if (res.type === SUCCESS_RESPONSE_TYPE) {
          const workspace = res.data.workspaces.find((w: any) => w.id.toString() === workspaceIdFromParams)
          if (workspace) {
            setLandingWorkspace(workspace)
          }
        }
      })
    }
  }, [isSSOUser, workspaceIdFromParams])

  const { onLoginOrSignUpSuccess: onLoginSuccess } = useLoginOrSignUp()

  const processingLabel = useMemo(
    () => (accountAlreadyExists ? 'Signing in' : 'Creating account'),
    [accountAlreadyExists],
  )

  const isValidEmail = (value: string) => emailRegex.test(value)

  const siderImageBase = useMemo(() => {
    if (invitedWorkspace?.is_whitelabel) return null

    if (stage === 'verifyCode') return `${BASE_ASSET_URL}/1.jpeg`
    if (stage === 'createAccount' || stage === 'existingUser' || stage === 'referrer' || stage === 'businessType')
      return `${BASE_ASSET_URL}/2.jpeg`
    // show the first sider on the base route too
    return `${BASE_ASSET_URL}/1.jpeg`
  }, [stage, invitedWorkspace])

  const handleLogin = useCallback(
    async (email: string, password: string) => {
      if (!email) return

      setIsProcessing(true)

      const res = await client.login(email, password)

      if (res.type === SUCCESS_RESPONSE_TYPE) {
        const auth = authFromResponse(res.data.data, res.headers)

        await onLoginSuccess(auth, RegistrationType.EmailAndPassword, redirectTo, workspaceInviteToken, promotionCode)

        queryClient.invalidateQueries('featureFlags')
        setIsProcessing(false)
      } else {
        setIsProcessing(false)
        showToastMessage('An account with this email already exists, try logging in instead.', 'error')
        history.push(LOGIN_PATH)

        return
      }
    },
    [onLoginSuccess, redirectTo, workspaceInviteToken, promotionCode, queryClient, history],
  )

  const siderImageSources = useMemo(() => {
    if (!siderImageBase) return undefined

    const aspectRatio = 1322 / 539 // target aspect ratio taken from Lighthouse report
    const heightsForWidth = (width: number) => Math.round(width * aspectRatio)

    const applyTransform = (width: number, format: 'avif' | 'webp' | 'auto') =>
      siderImageBase.replace(
        '/upload/',
        `/upload/f_${format},q_auto:eco,c_fill,g_auto,dpr_auto,w_${width},h_${heightsForWidth(width)}/`,
      )

    const baseWidth = 560

    const buildSources = (format: 'avif' | 'webp' | 'auto') => ({
      src: applyTransform(baseWidth, format),
      srcSet: [baseWidth, 1040].map((width) => `${applyTransform(width, format)} ${width}w`).join(', '),
      sizes: '(max-width: 1200px) 32vw, 520px',
    })

    return {
      avif: buildSources('avif'),
      webp: buildSources('webp'),
      fallback: buildSources('auto'),
    }
  }, [siderImageBase])

  const passwordRequirements = useMemo(
    () => [
      {
        validator: (value: string) => value.length >= 8,
        message: '8 characters',
      },
      {
        validator: (value: string) => /\d/.test(value),
        message: 'One number',
      },
      {
        validator: (value: string) => /[A-Z]/.test(value),
        message: 'One capital letter',
      },
    ],
    [],
  )

  const handleEmailSubmit = useCallback(
    async (email: string) => {
      if (!isValidEmail(email)) {
        setEmailError('Invalid email address')
        return
      }

      setEmailError('')
      setApiError('')
      setIsProcessing(true)
      localStorage.setItem(IS_VM_REGISTRATION_KEY, 'true')

      try {
        const res = await UserClient.verifyUserExists(email, redirectTo, true, true, workspaceInviteToken)

        if (res.data.success) {
          trackContinueWithSignUp()

          if (res.data.user_exists) {
            setStage('existingUser')
          } else {
            setStage('verifyCode')
          }
        } else {
          setApiError('Something went wrong – please try again.')
        }
      } catch (error) {
        setApiError('Something went wrong – please try again.')
      } finally {
        setIsProcessing(false)
      }
    },
    [redirectTo, workspaceInviteToken],
  )

  const handleEmailAndPasswordSubmit = useCallback(
    async (email: string, password: string) => {
      if (!isValidEmail(email)) {
        setEmailError('Invalid email address')
        return
      }

      if (!password.length) {
        setApiError('Password is required')
        return
      }

      setEmailError('')
      setApiError('')
      setIsProcessing(true)
      localStorage.setItem(IS_VM_REGISTRATION_KEY, 'true')

      try {
        const res = await UserClient.verifyUserExists(email, redirectTo, true, true, workspaceInviteToken)

        if (res.data.success) {
          trackContinueWithSignUp()

          if (res.data.user_exists) {
            setIsProcessing(true)
            setAccountAlreadyExists(true)
            await handleLogin(email, password)
          } else {
            setStage('verifyCode')

            setIsProcessing(false)
          }
        } else {
          setApiError('Something went wrong – please try again.')
        }
      } catch (error) {
        setApiError('Something went wrong – please try again.')
      } finally {
        setIsProcessing(false)
      }
    },
    [handleLogin, redirectTo, workspaceInviteToken],
  )

  const handleVerifyCode = useCallback(
    async (code: string) => {
      if (!code.trim().length) {
        setCodeError('Enter the code from your email')
        return
      }

      setCodeError('')
      setIsProcessing(true)
      try {
        const resp = await UserClient.verifyEmailConfirmationCode(emailInput, code)

        if (resp.type === SUCCESS_RESPONSE_TYPE) {
          trackVerifyWithCodeComplete()
          setVerificationCode(code)

          if (isOnboardingRedesign) {
            const firstName = emailInput.split('@')[0]

            try {
              const res = await UserClient.registerUser(
                firstName,
                '',
                password,
                undefined,
                code,
                emailInput,
                workspaceInviteToken,
                redirectTo,
              )

              if (res.type !== SUCCESS_RESPONSE_TYPE || !res.data.success) {
                setAccountError('An error occurred. Please try again.')
                setIsProcessing(false)
                return
              }

              const auth = authFromResponse(res.data.user, res.headers)
              setAuthInfo(auth)
              const { landingWorkspace: workspace } = await onSignUpSuccess(auth, workspaceInviteToken)
              setLandingWorkspace(workspace)
            } catch (error) {
              setAccountError('An error occurred. Please try again.')
            } finally {
              setIsProcessing(false)
            }

            setStage('businessType')
          } else {
            setStage('createAccount')
          }
          return
        }

        setCodeError('The code you entered does not match our records')
        setVerificationCode('')
        setClearInputKey((prev) => prev + 1)
      } finally {
        setIsProcessing(false)
      }
    },
    [emailInput, isOnboardingRedesign, onSignUpSuccess, password, redirectTo, workspaceInviteToken],
  )

  const handleExistingUserLogin = useCallback(
    async (passwordInput: string) => {
      if (!passwordInput.length) {
        setApiError('Password is required')
        return
      }

      setApiError('')
      setIsProcessing(true)

      try {
        const res = await client.login(emailInput, passwordInput)

        if (res.type !== SUCCESS_RESPONSE_TYPE) {
          setApiError('An error occurred. Please try again.')
          setIsProcessing(false)
          return
        }

        const authInfo = authFromResponse(res.data.data, res.headers)
        const { landingWorkspace } = await onSignUpSuccess(authInfo, workspaceInviteToken)
        const workspaceId = landingWorkspace?.id || authInfo.last_workspace_id || authInfo.company_id

        if (workspaceId) {
          hasNavigatedRef.current = true
          history.push(generatePath(VIRTUAL_MARKETER_ONBOARDING_PATH, { workspaceId }))
        } else {
          hasNavigatedRef.current = true
          history.push(SELECT_WORKSPACE_PATH)
        }
      } catch (error) {
        setApiError('An error occurred. Please try again.')
      } finally {
        setIsProcessing(false)
      }
    },
    [emailInput, history, onSignUpSuccess, workspaceInviteToken],
  )

  const handleCreateAccount = useCallback(
    async (fullName: string, passwordInput: string) => {
      setNameError('')
      setPasswordError('')
      setAccountError('')
      if (!fullName.trim().length) {
        setNameError('Full name is required')
        return
      }

      if (!passwordInput.length) {
        setPasswordError('Password is required')
        return
      }

      if (passwordInput.length < 8) {
        setPasswordError('Password must be at least 8 characters')
        return
      }

      if (!/\d/.test(passwordInput)) {
        setPasswordError('Password must contain a number')
        return
      }

      if (!/[A-Z]/.test(passwordInput)) {
        setPasswordError('Password must contain an uppercase letter')
        return
      }

      setIsProcessing(true)

      const [firstName, ...rest] = fullName.trim().split(' ')
      const lastName = rest.join(' ')

      try {
        const res = await UserClient.registerUser(
          firstName,
          lastName,
          passwordInput,
          undefined,
          verificationCode,
          emailInput,
          workspaceInviteToken,
          redirectTo,
        )

        if (res.type !== SUCCESS_RESPONSE_TYPE || !res.data.success) {
          setAccountError('An error occurred. Please try again.')
          setIsProcessing(false)
          return
        }

        const auth = authFromResponse(res.data.user, res.headers)
        setAuthInfo(auth)
        const { landingWorkspace: workspace } = await onSignUpSuccess(auth, workspaceInviteToken)
        setLandingWorkspace(workspace)

        if (invitedWorkspace?.is_whitelabel) {
          const workspaceId = workspace?.id || auth.last_workspace_id || auth.company_id
          if (workspaceId) {
            hasNavigatedRef.current = true
            history.push(generatePath(VIRTUAL_MARKETER_ONBOARDING_PATH, { workspaceId }))
          }
        } else {
          setStage('referrer')
        }
      } catch (error) {
        setAccountError('An error occurred. Please try again.')
      } finally {
        setIsProcessing(false)
      }
    },
    [
      emailInput,
      verificationCode,
      onSignUpSuccess,
      workspaceInviteToken,
      redirectTo,
      invitedWorkspace,
      history,
      hasNavigatedRef,
    ],
  )

  const handleVirtualMarketerOnboardingRedirect = useCallback(async () => {
    if (!authInfo) return
    const workspaceId = landingWorkspace?.id || authInfo.last_workspace_id || authInfo.company_id
    if (workspaceId) {
      hasNavigatedRef.current = true
      history.push({
        pathname: generatePath(VIRTUAL_MARKETER_ONBOARDING_PATH, { workspaceId }),
        search: location.search,
      })
    } else {
      const nextParams = new URLSearchParams()
      nextParams.set('email', emailInput)
      nextParams.set('needs_onboarding', 'true')
      if (workspaceInviteToken) nextParams.set('workspaceInviteToken', workspaceInviteToken)
      if (redirectTo) nextParams.set('redirectTo', redirectTo)
      if (promotionCode) nextParams.set('promotionCode', promotionCode)
      hasNavigatedRef.current = true
      history.push({
        pathname: AUTOPILOT_REGISTRATION_PATH,
        search: nextParams.toString(),
      })
    }
  }, [
    authInfo,
    landingWorkspace?.id,
    history,
    location.search,
    emailInput,
    workspaceInviteToken,
    redirectTo,
    promotionCode,
  ])

  const handleReferrerNext = useCallback(() => {
    if (isOnboardingRedesign) {
      setStage('getStarted')
      return
    }

    setStage('businessType')
  }, [isOnboardingRedesign])

  const handleGetStartedNext = useCallback(() => {
    handleVirtualMarketerOnboardingRedirect()
  }, [handleVirtualMarketerOnboardingRedirect])

  const handleReferrerBack = useCallback(() => {
    if (isOnboardingRedesign) {
      setStage('businessType')
    } else {
      setStage('createAccount')
    }
  }, [isOnboardingRedesign])

  const handleBusinessTypeNext = useCallback(async () => {
    if (isOnboardingRedesign) {
      setStage('referrer')
      return
    }

    handleVirtualMarketerOnboardingRedirect()
  }, [isOnboardingRedesign, handleVirtualMarketerOnboardingRedirect])

  const handleBusinessTypeBack = useCallback(() => {
    setStage('referrer')
  }, [])

  if (hasNavigatedRef.current) {
    return null
  }

  const handleLogout = () => {
    logout({ logoutRedirect: null })
    setStage('start')
  }

  const renderButtons = () => (
    <div className={cx(RegistrationStyles.buttons, Styles.signupButtons)}>
      <GoogleOAuthButton workspaceInviteToken={workspaceInviteToken || ''} checkNewUser />
      <FacebookOAuthButton workspaceInviteToken={workspaceInviteToken || ''} checkNewUser />
      <AppleOAuthButton workspaceInviteToken={workspaceInviteToken || ''} checkNewUser />

      {isOnboardingRedesign ? (
        <>
          <div className={Styles.orSeparator}>
            <div className={Styles.orSeparatorLine} />
            <Text variant="label" color="var(--dark-40)">
              Or sign up with email
            </Text>
            <div className={Styles.orSeparatorLine} />
          </div>
          <form
            className={cx(CreateAccountStyles.form, Styles.form)}
            onSubmit={(e) => {
              e.preventDefault()
              handleEmailAndPasswordSubmit(emailInput, password)
            }}
          >
            <div className={cx(CreateAccountStyles.fields, Styles.fields)}>
              <div className={cx(CreateAccountStyles.fieldGroup, Styles.fieldGroup)}>
                <label htmlFor="email">
                  <Text variant="label" color="var(--dark-60)">
                    Email
                  </Text>
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  size="xl"
                  placeholder="Continue with Email"
                  autoFocus
                  value={emailInput}
                  onChange={(e) => setEmailInput((e.target as HTMLInputElement).value)}
                  isValid={!emailError}
                />
                {emailError && (
                  <div className={Styles.error}>
                    <FormErrorMessage>{emailError}</FormErrorMessage>
                  </div>
                )}
              </div>
              <div className={cx(CreateAccountStyles.fieldGroup, Styles.fieldGroup)}>
                <label htmlFor="password">
                  <Text variant="label" color="var(--dark-60)">
                    Password
                  </Text>
                </label>
                <Input
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => {
                    setPassword((e.target as HTMLInputElement).value)
                    setPasswordError('')
                    setAccountError('')
                  }}
                  size="xl"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter Password"
                />
                {password && (
                  <div className={Styles.showPasswordButton}>
                    {isOnboardingRedesign ? (
                      <IconButton
                        onPress={() => setShowPassword(!showPassword)}
                        variant="tertiary"
                        icon={showPassword ? EyeClosed : EyeOpen}
                        color="var(--dark-40)"
                      />
                    ) : (
                      <Button variant="tertiary" fullWidth onPress={() => setShowPassword(!showPassword)}>
                        <Text variant="label" color="var(--dark-40)">
                          {showPassword ? 'Hide' : 'Show'}
                        </Text>
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {password && (
                <div className={CreateAccountStyles.passwordValidationContainer}>
                  <Text variant="label" color="var(--dark-40)">
                    Password must contain at least:
                  </Text>
                  <div className={CreateAccountStyles.requirementOptions}>
                    {passwordRequirements.map(({ validator, message }, idx) => (
                      <div className={CreateAccountStyles.passwordRequirement} key={`validator-${idx}`}>
                        {validator(password) ? (
                          <Check className={CreateAccountStyles.success} size={14} color="var(--green-50)" />
                        ) : (
                          <Close size={14} color="#AE2222" />
                        )}
                        <Text variant="metadata">{message}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Button
                type="submit"
                variant="primary"
                size="xl"
                fullWidth
                isDisabled={
                  isProcessing ||
                  !emailInput.length ||
                  !password.length ||
                  passwordRequirements.some(({ validator }) => !validator(password))
                }
              >
                {isProcessing ? (
                  <>
                    {processingLabel}
                    <LoadingEllipses />
                  </>
                ) : (
                  'Sign up'
                )}
              </Button>
            </div>
          </form>
        </>
      ) : (
        <>
          {showEmailInput ? (
            <form
              className={Styles.emailForm}
              onSubmit={(e) => {
                e.preventDefault()
                handleEmailSubmit(emailInput)
              }}
            >
              <div className={cx(RegistrationStyles.emailField, Styles.emailField)}>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  size="xl"
                  placeholder="Continue with Email"
                  autoFocus
                  value={emailInput}
                  onChange={(e) => setEmailInput((e.target as HTMLInputElement).value)}
                  isValid={!emailError}
                />
                {emailInput.length > 0 && (
                  <button
                    type="submit"
                    className={RegistrationStyles.inlineSubmitButton}
                    aria-label="Submit email"
                    disabled={isProcessing}
                  >
                    <RightArrowIcon />
                  </button>
                )}
              </div>
              {emailError && (
                <div className={Styles.error}>
                  <FormErrorMessage>{emailError}</FormErrorMessage>
                </div>
              )}
            </form>
          ) : (
            <Button
              variant="tertiary"
              size="xl"
              fullWidth
              useChildClass
              frontIcon={Email}
              onPress={() => setShowEmailInput(true)}
            >
              <div className={RegistrationStyles.registrationButton}>Continue with Email</div>
            </Button>
          )}
        </>
      )}
      {apiError && (
        <div className={Styles.error}>
          <FormErrorMessage>{apiError}</FormErrorMessage>
        </div>
      )}
    </div>
  )

  const renderVerifyCode = () => (
    <div className={Styles.stepWrapper}>
      <div className={Styles.stepInner}>
        <MultiStepStepLayout
          title="Verify your email"
          description={
            <div className={cx(VerifyStyles.description, Styles.description)}>
              <Text color="var(--dark-60)">Enter the code we sent to {emailInput} to finish signing up.</Text>
              <Text color="var(--dark-60)">
                {isOnboardingRedesign ? (
                  <span>
                    <span className={Styles.bold}>Check your Spam</span> folder if you don't see it.
                  </span>
                ) : (
                  <span>
                    Check your <b>Spam folder</b> in case you don't see it.
                  </span>
                )}
              </Text>
            </div>
          }
          centerContent
          scrollContainerClassName={VerifyStyles.scrollContainer}
          contentClassName={Styles.stepContent}
        >
          <div className={cx(VerifyStyles.container, Styles.codeInputContainer, { [Styles.hasError]: codeError })}>
            <div className={Styles.codeInputWrapper}>
              <CodeInput
                clearInput={clearInputKey}
                disabled={isProcessing}
                onChange={() => {
                  setCodeError('')
                }}
                onComplete={(value: string) => {
                  setVerificationCode(value)
                  handleVerifyCode(value)
                }}
              />
            </div>
            {codeError && (
              <div className={Styles.errorText}>
                <FormErrorMessage>{codeError}</FormErrorMessage>
              </div>
            )}
            <div className={VerifyStyles.resendEmail}>
              <Button
                type="button"
                variant={isOnboardingRedesign ? 'secondary' : 'tertiary'}
                size={isOnboardingRedesign ? 'lg' : 'xl'}
                isDisabled={isProcessing}
                onPress={() => {
                  UserClient.sendEmail(emailInput, 'email_verification', redirectTo, workspaceInviteToken)
                  setVerificationCode('')
                  setClearInputKey((prev) => prev + 1)

                  if (isOnboardingRedesign) {
                    showToastMessage('We sent you a new verification code, please check your inbox.', 'success')
                  }
                }}
              >
                {isProcessing ? (
                  <>
                    Creating account
                    <LoadingEllipses />
                  </>
                ) : (
                  'Resend email'
                )}
              </Button>
            </div>
          </div>
        </MultiStepStepLayout>
      </div>
    </div>
  )

  const renderExistingUser = () => (
    <div className={Styles.stepWrapper}>
      <div className={Styles.stepInner}>
        <MultiStepStepLayout
          title="Enter password"
          centerContent
          contentClassName={Styles.stepContent}
          scrollContainerClassName={EnterPasswordStyles.scrollContainer}
        >
          <form
            className={cx(Styles.formCard, Styles.centeredCard)}
            onSubmit={(e) => {
              e.preventDefault()
              handleExistingUserLogin(password)
            }}
          >
            <div className={cx(EnterPasswordStyles.fields, Styles.formFields)}>
              <div className={cx(EnterPasswordStyles.fieldGroup, Styles.fieldGroup)}>
                <label htmlFor="existingEmail">
                  <Text
                    variant={isOnboardingRedesign ? 'label' : 'smallList'}
                    color={isOnboardingRedesign ? 'var(--dark-60)' : 'var(--dark-80)'}
                  >
                    Email Address
                    <span className={EnterPasswordStyles.required}>*</span>
                  </Text>
                </label>
                <Input
                  id="existingEmail"
                  name="existingEmail"
                  value={emailInput}
                  onChange={(e) => setEmailInput((e.target as HTMLInputElement).value)}
                  size="xl"
                  type="email"
                  placeholder="Email Address"
                />
              </div>
              <div className={cx(EnterPasswordStyles.fieldGroup, Styles.fieldGroup)}>
                <label htmlFor="existingPassword">
                  <Text
                    variant={isOnboardingRedesign ? 'label' : 'smallList'}
                    color={isOnboardingRedesign ? 'var(--dark-60)' : 'var(--dark-80)'}
                  >
                    Password
                    <span className={EnterPasswordStyles.required}>*</span>
                  </Text>
                </label>
                <Input
                  id="existingPassword"
                  name="existingPassword"
                  value={password}
                  onChange={(e) => setPassword((e.target as HTMLInputElement).value)}
                  size="xl"
                  type="password"
                  placeholder="Enter Password"
                  autoFocus
                />
              </div>
              {apiError && (
                <div className={Styles.error}>
                  <FormErrorMessage>{apiError}</FormErrorMessage>
                </div>
              )}
              <div className={Styles.buttons}>
                <Button type="submit" variant="primary" size="xl" fullWidth isDisabled={isProcessing}>
                  Continue
                </Button>
                <div className={cx(EnterPasswordStyles.passwordResetContainer, Styles.passwordResetContainer)}>
                  <span>Forgot your password?</span>
                  <button
                    type="button"
                    className={cx(EnterPasswordStyles.resetIt, Styles.resetIt)}
                    onClick={() => UserClient.sendEmail(emailInput, 'password_reset', redirectTo, workspaceInviteToken)}
                  >
                    Reset it
                  </button>
                </div>
              </div>
            </div>
          </form>
        </MultiStepStepLayout>
      </div>
    </div>
  )

  const renderCreateAccount = () => (
    <div className={Styles.stepWrapper}>
      <div className={Styles.stepInner}>
        <MultiStepStepLayout
          title={invitedWorkspace?.is_whitelabel ? 'Create your account' : 'Create your Autopilot account'}
          centerContent
          scrollContainerClassName={CreateAccountStyles.scrollContainer}
          contentClassName={Styles.stepContent}
        >
          <form
            className={CreateAccountStyles.form}
            onSubmit={(e) => {
              e.preventDefault()
              handleCreateAccount(name, password)
            }}
          >
            <div className={CreateAccountStyles.fields}>
              <div className={CreateAccountStyles.fieldGroup}>
                <label htmlFor="name">
                  <Text variant="smallList" color="var(--dark-80)">
                    Full Name
                    <span className={CreateAccountStyles.required}>*</span>
                  </Text>
                </label>
                <Input
                  id="name"
                  name="name"
                  value={name}
                  onChange={(e) => {
                    setName((e.target as HTMLInputElement).value)
                    setNameError('')
                    setAccountError('')
                  }}
                  size="xl"
                  placeholder="John Doe"
                  autoFocus
                />
                {nameError && (
                  <div className={Styles.error}>
                    <FormErrorMessage className={CreateAccountStyles.errorMessage}>{nameError}</FormErrorMessage>
                  </div>
                )}
              </div>
              <div className={CreateAccountStyles.fieldGroup}>
                <label htmlFor="password">
                  <Text variant="smallList" color="var(--dark-80)">
                    Password
                    <span className={CreateAccountStyles.required}>*</span>
                  </Text>
                </label>
                <Input
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => {
                    setPassword((e.target as HTMLInputElement).value)
                    setPasswordError('')
                    setAccountError('')
                  }}
                  size="xl"
                  type="password"
                  placeholder="Enter Password"
                />
                {passwordError && (
                  <div className={Styles.error}>
                    <FormErrorMessage className={CreateAccountStyles.errorMessage}>{passwordError}</FormErrorMessage>
                  </div>
                )}
              </div>
              <div className={CreateAccountStyles.passwordValidationContainer}>
                <Text variant="label" color="var(--dark-80)">
                  Password must contain at least:
                </Text>
                <div className={CreateAccountStyles.requirementOptions}>
                  {passwordRequirements.map(({ validator, message }, idx) => (
                    <div className={CreateAccountStyles.passwordRequirement} key={`validator-${idx}`}>
                      {validator(password) ? (
                        <Check className={CreateAccountStyles.success} size={14} color="var(--green-50)" />
                      ) : (
                        <Close size={14} color="var(--dark-80)" />
                      )}
                      <Text variant="metadata">{message}</Text>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {accountError && (
              <div className={Styles.error}>
                <FormErrorMessage className={CreateAccountStyles.errorMessage}>{accountError}</FormErrorMessage>
              </div>
            )}
            <Button type="submit" variant="primary" size="lg" fullWidth isDisabled={isProcessing}>
              {isProcessing ? 'Creating account...' : 'Continue'}
            </Button>
          </form>
        </MultiStepStepLayout>
      </div>
    </div>
  )

  const isStart = stage === 'start'
  const hasSider = !!siderImageSources?.fallback.src && !isOnboardingRedesign

  if (!featureFlags.ready) {
    featureFlags.load()

    return (
      <div className={Styles.spinnerContainer}>
        <Spinner />
      </div>
    )
  }

  return (
    <div
      data-autopilot-system-redesign={isOnboardingRedesign ? 'on' : 'off'}
      className={cx(
        Styles.root,
        hasSider && Styles.withSider,
        !isStart && Styles.compactRoot,
        isStart && Styles.startRoot,
      )}
    >
      {isOnboardingRedesign && ['verifyCode', 'referrer', 'businessType', 'getStarted'].includes(stage) && (
        <div className={Styles.signOutButton}>
          <Button variant="tertiary" onPress={handleLogout}>
            Sign Out
          </Button>
        </div>
      )}
      {siderImageSources && (
        <Helmet>
          <link
            rel="preload"
            as="image"
            href={siderImageSources.avif?.src || siderImageSources.fallback.src}
            imageSrcSet={siderImageSources.avif?.srcSet || siderImageSources.fallback.srcSet}
            imageSizes={siderImageSources.avif?.sizes || siderImageSources.fallback.sizes}
          />
        </Helmet>
      )}
      <div className={cx(Styles.contentShell, hasSider && Styles.compactLayout, isStart && Styles.startContentShell)}>
        <div className={cx(Styles.mainColumn, isStart && Styles.startMainColumn)}>
          {isStart ? (
            <div className={cx(RegistrationStyles.container, Styles.startContainer)}>
              {isOnboardingRedesign ? (
                <div className={Styles.welcomeToBlaze}>
                  <Heading level={1}>Welcome to Blaze!</Heading>
                  <Text color="var(--dark-60)">
                    Already have an account?{' '}
                    <button
                      className={Styles.loginLink}
                      data-test-id="switch-login"
                      onClick={() => history.replace({ pathname: LOGIN_PATH, search: location.search })}
                    >
                      Login
                    </button>
                  </Text>
                </div>
              ) : (
                <>
                  <ResponsiveDesign.Desktop>
                    <div className={RegistrationStyles.logo}>
                      {!(workspaceInviteToken && invitedWorkspaceLoading) && (
                        <WhiteLabelLogo workspace={invitedWorkspace} />
                      )}
                    </div>
                  </ResponsiveDesign.Desktop>
                  <h1 className={RegistrationStyles.title}>
                    {invitedWorkspace?.is_whitelabel && invitedWorkspace?.name
                      ? `Join ${invitedWorkspace.name}`
                      : 'Set Your Marketing to Autopilot'}
                  </h1>
                  {!invitedWorkspace?.is_whitelabel && (
                    <span className={cx(RegistrationStyles.alreadyHaveAccount, Styles.subtitle)}>
                      Start your 7-day free trial and get weeks of ready-to-publish posts that sound exactly like you.{' '}
                      <b>No credit card required.</b>
                    </span>
                  )}
                </>
              )}
              <div className={Styles.stageArea}>{renderButtons()}</div>
              {!isOnboardingRedesign && (
                <>
                  <span className={RegistrationStyles.alreadyHaveAccount}>
                    Already have an account?{' '}
                    <button
                      className={RegistrationStyles.login}
                      data-test-id="switch-login"
                      onClick={() => history.push(LOGIN_PATH)}
                    >
                      Login
                    </button>
                  </span>
                </>
              )}
              <div className={cx(RegistrationStyles.footerText, Styles.footerText)}>
                By signing up, you agree to the <br />
                <a href="https://www.blaze.ai/terms" target="_blank" rel="noreferrer">
                  Terms of Service{isOnboardingRedesign ? '' : ' →'}
                </a>
                {', '}
                and{' '}
                <a href="https://www.blaze.ai/privacy-policy" target="_blank" rel="noreferrer">
                  Privacy Policy{isOnboardingRedesign ? '' : ' →'}
                </a>
                .
              </div>
            </div>
          ) : (
            <>
              {!isOnboardingRedesign && (
                <div className={Styles.globalLogo}>
                  {!(workspaceInviteToken && invitedWorkspaceLoading) && (
                    <WhiteLabelLogo workspace={invitedWorkspace} />
                  )}
                </div>
              )}
              <div className={cx(Styles.signupCard, Styles.compactCard)}>
                <div className={Styles.stepWrapper}>
                  {stage === 'verifyCode' && renderVerifyCode()}
                  {stage === 'existingUser' && renderExistingUser()}
                  {stage === 'createAccount' && renderCreateAccount()}
                  {stage === 'referrer' && <Referrer onNext={handleReferrerNext} onBack={handleReferrerBack} />}
                  {stage === 'getStarted' && <GetStarted onNext={handleGetStartedNext} />}
                  {stage === 'businessType' && (
                    <BusinessType onNext={handleBusinessTypeNext} onBack={handleBusinessTypeBack} />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        {isOnboardingRedesign && isStart && !isMobile && (
          <img
            className={Styles.blazeIllustration}
            alt="blaze illustration"
            src="https://res.cloudinary.com/almanac/image/upload/v1765477725/blaze_assets/virtual_marketer/onboarding/Frame_1171276883_x1tth2.png"
          />
        )}
      </div>
      {siderImageSources && !isOnboardingRedesign && (
        <div className={Styles.siderImageContainer}>
          <picture>
            <source type="image/avif" srcSet={siderImageSources.avif.srcSet} sizes={siderImageSources.avif.sizes} />
            <source type="image/webp" srcSet={siderImageSources.webp.srcSet} sizes={siderImageSources.webp.sizes} />
            <img
              src={siderImageSources.fallback.src}
              srcSet={siderImageSources.fallback.srcSet}
              sizes={siderImageSources.fallback.sizes}
              alt="Registration illustration"
              className={Styles.siderImage}
              loading="eager"
            />
          </picture>
        </div>
      )}
    </div>
  )
})

export default Signup
